import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUserStore } from "@/store/userStore";
import { useAccountingMutations } from "@/hooks/useAccounting";
import { AccountingTreasuryTab } from "@/components/admin/accounting/AccountingTreasuryTab";
import { ExpensesJournalTab } from "@/components/admin/accounting/ExpensesJournalTab";
import { SuppliersTab } from "@/components/admin/accounting/SuppliersTab";
import { RecurringExpensesTab } from "@/components/admin/accounting/RecurringExpensesTab";
import { UpcomingTab } from "@/components/admin/accounting/UpcomingTab";

const iso = (x: Date) =>
  `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;

/** Bornes yyyy-MM-dd d'un mois donné (Date locale). */
export function monthBounds(d: Date) {
  return {
    start_date: iso(new Date(d.getFullYear(), d.getMonth(), 1)),
    end_date: iso(new Date(d.getFullYear(), d.getMonth() + 1, 0)),
  };
}

/** Bornes yyyy-MM-dd d'une année entière. */
export function yearBounds(year: number) {
  return {
    start_date: iso(new Date(year, 0, 1)),
    end_date: iso(new Date(year, 11, 31)),
  };
}

type PeriodMode = "month" | "year";

const Accounting = () => {
  const { t, i18n } = useTranslation(["accounting", "common"]);
  const gymId = useUserStore((s) => s.gymId);
  const { generateRecurring } = useAccountingMutations();

  // Deux modes de période : une année entière, ou un mois précis d'une année.
  const now = new Date();
  const [mode, setMode] = useState<PeriodMode>("month");
  const [year, setYear] = useState(() => now.getFullYear());
  const [month, setMonth] = useState(() => now.getMonth()); // 0-11

  const period = useMemo(
    () => (mode === "year" ? yearBounds(year) : monthBounds(new Date(year, month, 1))),
    [mode, year, month]
  );

  const locale = i18n.language === "fr" ? "fr-FR" : "en-US";
  const yearOptions = useMemo(() => {
    const current = now.getFullYear();
    return Array.from({ length: 7 }, (_, i) => current + 1 - i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, m) => ({
        value: m,
        label: new Date(2000, m, 1).toLocaleDateString(locale, { month: "long" }),
      })),
    [locale]
  );

  // Génération des occurrences récurrentes du mois visé — idempotente côté
  // edge, fire-and-forget, une fois par couple salle|mois (changer de mois ou
  // de salle déclenche sa génération). En mode année, on vise le mois courant.
  const generatedRef = useRef(new Set<string>());
  const generationMonth =
    mode === "year"
      ? `${year}-${String(year === now.getFullYear() ? now.getMonth() + 1 : 12).padStart(2, "0")}`
      : period.start_date.slice(0, 7);
  useEffect(() => {
    if (!gymId) return;
    const key = `${gymId}|${generationMonth}`;
    if (generatedRef.current.has(key)) return;
    generatedRef.current.add(key);
    generateRecurring.mutate(generationMonth, { onError: () => {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gymId, generationMonth]);

  return (
    <div className="min-h-screen flex flex-col w-full">
      <main className="flex-1 p-3 md:p-6 max-w-7xl mx-auto w-full animate-fade-in space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-3xl font-bold tracking-tight">{t("title")}</h1>
            <p className="text-xs md:text-sm text-muted-foreground">{t("subtitle")}</p>
          </div>
          {/* Sélecteur de période partagé par les onglets : année entière ou mois précis */}
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center">
            <Select value={mode} onValueChange={(v) => setMode(v as PeriodMode)}>
              <SelectTrigger className="h-10 w-full sm:h-9 sm:w-[150px]" aria-label={t("period.mode")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">{t("period.modeMonth")}</SelectItem>
                <SelectItem value="year">{t("period.modeYear")}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-10 w-full sm:h-9 sm:w-[110px]" aria-label={t("period.year")}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {mode === "month" && (
              <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger className="col-span-2 h-10 w-full capitalize sm:h-9 sm:w-[140px]" aria-label={t("period.month")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((m) => (
                    <SelectItem key={m.value} value={String(m.value)} className="capitalize">
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <Tabs defaultValue="treasury">
          <TabsList className="w-full justify-start overflow-x-auto md:w-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <TabsTrigger value="treasury" className="shrink-0">{t("tabs.treasury")}</TabsTrigger>
            <TabsTrigger value="journal" className="shrink-0">{t("tabs.journal")}</TabsTrigger>
            <TabsTrigger value="suppliers" className="shrink-0">{t("tabs.suppliers")}</TabsTrigger>
            <TabsTrigger value="expenses" className="shrink-0">{t("tabs.expenses")}</TabsTrigger>
            <TabsTrigger value="upcoming" className="shrink-0">{t("tabs.upcoming")}</TabsTrigger>
          </TabsList>

          <TabsContent value="treasury" className="mt-4">
            <AccountingTreasuryTab period={period} />
          </TabsContent>
          <TabsContent value="journal" className="mt-4">
            <ExpensesJournalTab period={period} />
          </TabsContent>
          <TabsContent value="suppliers" className="mt-4">
            <SuppliersTab />
          </TabsContent>
          <TabsContent value="expenses" className="mt-4">
            <RecurringExpensesTab period={period} generationMonth={generationMonth} />
          </TabsContent>
          <TabsContent value="upcoming" className="mt-4">
            <UpcomingTab period={period} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Accounting;
